import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LifeBuoy,
  Headset,
  BookOpen,
  BarChart2,
  Bell,
  Users,
  Compass,
  KeyRound,
  Mail,
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import './Help.css';

const Help = () => {
  const { currentUser } = useAppContext();
  const navigate = useNavigate();
  const role = currentUser?.role || 'Trainee';
  const isTrainee = role === 'Trainee';
  const isAdmin = role === 'Administrator';

  return (
    <div className="help animate-fade-in">
      {/* Header */}
      <div className="help__banner">
        <div className="help__banner-icon">
          <LifeBuoy size={28} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="help__title">Help & Guide</h1>
          <p className="help__subtitle">
            Everything explained in plain language — no technical knowledge needed.
          </p>
        </div>
      </div>

      {/* Quick start */}
      <section className="help__section">
        <h2 className="help__section-title">Getting started</h2>
        <div className="help__cards">
          {isTrainee ? (
            <>
              <div className="help__card">
                <BookOpen size={20} className="help__card-icon" />
                <h3>1. Find your training</h3>
                <p>
                  Open <button className="help__link" onClick={() => navigate('/modules')}>Modules</button> from
                  the menu on the left. You'll see the training modules you've been enrolled in,
                  with your progress on each one.
                </p>
              </div>
              <div className="help__card">
                <Headset size={20} className="help__card-icon" />
                <h3>2. Sign in on the VR headset</h3>
                <p>
                  The VR headset asks for a short <strong>access token</strong> instead of a password.
                  Find yours under <button className="help__link" onClick={() => navigate('/profile')}>My Profile → Access Token</button>.
                  Type it into the headset and your training will load.
                </p>
              </div>
              <div className="help__card">
                <BarChart2 size={20} className="help__card-icon" />
                <h3>3. Track your progress</h3>
                <p>
                  After each VR session, your scores and completed lessons appear in{' '}
                  <button className="help__link" onClick={() => navigate('/analytics')}>Analytics</button>.
                  Nothing to submit — it updates automatically.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="help__card">
                <Users size={20} className="help__card-icon" />
                <h3>1. Add and enroll people</h3>
                <p>
                  Go to <button className="help__link" onClick={() => navigate('/enrollment')}>Enrollment</button> to
                  add trainees and assign them to training modules. Enrolled trainees automatically
                  get an email and an in-app notification.
                </p>
              </div>
              <div className="help__card">
                <BookOpen size={20} className="help__card-icon" />
                <h3>2. Build training modules</h3>
                <p>
                  A <strong>module</strong> is a course: a bundle of VR apps, videos, and tours. Create
                  one in <button className="help__link" onClick={() => navigate('/modules')}>Modules</button>,
                  then add content from the Content Library.
                </p>
              </div>
              <div className="help__card">
                <BarChart2 size={20} className="help__card-icon" />
                <h3>3. Watch progress live</h3>
                <p>
                  <button className="help__link" onClick={() => navigate('/analytics')}>Analytics</button> shows
                  completion rates per module and per trainee. Click any module row or trainee name to
                  drill into details, including in-VR assessment scores.
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Key concepts */}
      <section className="help__section">
        <h2 className="help__section-title">Common questions</h2>

        <details className="help__faq">
          <summary><KeyRound size={16} /> What is a VR access token?</summary>
          <p>
            It's a short code that works like a password, but is easy to type inside a VR headset.
            {isTrainee
              ? ' Yours is on your Profile page under "Access Token". If it stops working, you can regenerate it there — the old one becomes invalid.'
              : ' Each trainee and instructor has one on their Profile page. Admins can manage tokens in Settings.'}
          </p>
        </details>

        <details className="help__faq">
          <summary><Bell size={16} /> What does the bell icon do?</summary>
          <p>
            The bell in the top-right corner shows your notifications — for example when you're
            enrolled in a new module or new content is added to your training. Click a notification
            to jump straight to the related page.
          </p>
        </details>

        <details className="help__faq">
          <summary><Compass size={16} /> What are Virtual Tours?</summary>
          <p>
            Virtual Tours are 360° walkthroughs you can open right here in the browser — no headset
            needed. Find them under "Virtual Tours" in the menu.
          </p>
        </details>

        {!isTrainee && (
          <details className="help__faq">
            <summary><BarChart2 size={16} /> Why is a chart empty?</summary>
            <p>
              Detailed VR analytics (sessions, assessment scores, activity charts) appear once
              trainees start using the VR app — the headset reports their activity back to the LMS
              automatically. If a chart is empty, it usually just means no VR sessions have been
              recorded yet.
            </p>
          </details>
        )}

        <details className="help__faq">
          <summary><Mail size={16} /> I forgot my password — what do I do?</summary>
          <p>
            On the login screen, use the "Forgot password" option to receive a reset link by email.
            {isAdmin
              ? ' As an administrator, you can also reset any user’s password from Settings → Users.'
              : ' If you don’t receive an email, contact your administrator.'}
          </p>
        </details>
      </section>

      {/* Contact */}
      <section className="help__section">
        <div className="help__contact">
          <p>
            <strong>Still stuck?</strong>{' '}
            {isAdmin
              ? 'Contact ChenaiXR support for platform issues.'
              : 'Contact your administrator — they can fix accounts, enrollments, and access issues.'}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Help;
